import { ChevronRight } from "lucide-react";
import React from "react";

import {
  Badge,
  Card,
  CardContent,
} from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context/i18n";
import { formatCpfCnpj } from "@/core/utils";

import { ClientStatus, ETypeOfPartner, TClient } from "../clients.types";

interface ITableClientsProps {
  clients: TClient[];
  openClientReview: (clientId: string) => void;
  deleteClient: (id: string) => void;
  openClientConfigProducts: (open: boolean) => void;
  setSelectedClient: (client: TClient) => void;
}

export const TableClients: React.FC<ITableClientsProps> = ({
  clients,
  openClientReview,
}) => {
  const { t } = useI18n();

  const getBadgeVariant = (status: ClientStatus | undefined) => {
    switch (status) {
      case ClientStatus.approved:
        return "bg-green-500 text-white hover:bg-green-600";
      case ClientStatus.pending_data:
        return "bg-yellow-500 text-white hover:bg-yellow-600";
      case ClientStatus.pending_review:
        return "bg-orange-500 text-white hover:bg-orange-600";
      case ClientStatus.rejected:
        return "bg-red-500 text-white hover:bg-red-600";
    }
  };

  const getTypePartnerText = (typePartner: ETypeOfPartner | undefined) => {
    if (!typePartner) return "";
    if (typePartner === ETypeOfPartner.client) {
      return t("clients.form.fields.type.partner.client");
    }
    // Para business_indicator, retorna apenas "Hunter"
    return "Hunter";
  };

  const getTypePartnerBadgeVariant = (typePartner: ETypeOfPartner | undefined) => {
    switch (typePartner) {
      case ETypeOfPartner.client:
        return "bg-blue-500 text-white hover:bg-blue-600";
      case ETypeOfPartner.business_indicator:
        return "bg-purple-500 text-white hover:bg-purple-600";
      default:
        return "bg-gray-500 text-white hover:bg-gray-600";
    }
  };

  return (
    <div>
      <Card className="p-4">
        <CardContent>
          {clients.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-muted-foreground">{t("clients.noClients")}</p>
            </div>
          )}
          {clients.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("clients.form.fields.name")}</TableHead>
                  <TableHead>{t("clients.cnpj")}</TableHead>
                  <TableHead>{t("clients.corporate_name")}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {clients.map((client, i) => (
                  <TableRow 
                    key={i}
                    className="cursor-pointer hover:bg-muted/50 transition-colors group"
                    onClick={() => {
                      openClientReview(client.id);
                    }}
                  >
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>{formatCpfCnpj(client.cnpj)}</TableCell>
                    <TableCell>{client.corporate_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end">
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
